import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Loader,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_CRT_MED,
  CF_NEU_BLD,
  RENDER_MODES,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import { type TrackedListRowState } from '../../../../shared/types';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import { useGetGraphCluster } from '../../hooks';
import { getRowContext } from '../../utils';

import {
  ClusterCard,
  ViolationsInsightsFooter,
} from './ViolationsGraphClusterShared';
import ViolationsGraphSidePanel from './ViolationsGraphSidePanel';

import './ViolationsGraphCluster.css';

/**
 * ViolationsGraphCluster
 */
function ViolationsGraphCluster(): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const {
    global: { isReadOnlyAction },
    violationsTab: {
      controls: { activeGraphPoint, renderMode, activeRowContext },
    },
  } = useTypedSelector((state) => state.aag);

  const [clusterData, setClusterData] = useState<TrackedListRowState[] | null>(
    null
  );
  const [clusterTotalRecords, setClusterTotalRecords] = useState<number | null>(
    null
  );
  const isGraphMode = useMemo(
    () => Boolean(renderMode === RENDER_MODES.GRAPH),
    [renderMode]
  );

  const { handleFetchGraphCluster, isLoading, isError } = useGetGraphCluster();

  const triggerGraphCluster = useCallback(() => {
    if (activeGraphPoint) {
      setClusterData(null);

      handleFetchGraphCluster(activeGraphPoint.clusterId)
        .then((response) => {
          const typedResponse = response as {
            data: TrackedListRowState[] | null | undefined;
            page: { max: number; offset: number };
            totalRecords?: number;
          };

          if (typedResponse.data && typedResponse.data.length === 1) {
            const tempRowContext = getRowContext({
              ...typedResponse.data[0],
              tracker: {
                id: typedResponse.data[0].sodKey,
                isChecked: false,
                isRowDetailsOpen: false,
                isTransition: false,
                isFirstAddded: false,
                isReadOnlyAction,
              },
            });

            dispatch(violationsTabActions.setGraphRowContext(tempRowContext));

            return;
          }

          setClusterData(typedResponse.data ?? []);
          setClusterTotalRecords(typedResponse.totalRecords ?? null);
        })
        .catch((error) => {
          Logger.log('Error fetching graph cluster', error);
          setClusterTotalRecords(null);

          dispatch(violationsTabActions.setGraphRowContext(null));
        });
    }
  }, [activeGraphPoint, handleFetchGraphCluster, isReadOnlyAction, dispatch]);

  useEffect(() => {
    triggerGraphCluster();
  }, [dispatch, triggerGraphCluster, activeGraphPoint]);

  // Close and Back actions
  const handleClusterBack = useCallback(() => {
    setClusterData(null);
    dispatch(violationsTabActions.setActiveGraphPoint(null));
  }, [dispatch]);

  const handleClusterClose = useCallback(() => {
    dispatch(violationsTabActions.setIsInsightsOpen(false));
  }, [dispatch]);

  const renderBody = useCallback(() => {
    if (isError || clusterData?.length === 0) {
      return (
        <>
          <Box className='Sod-ViolationsGraphCluster-listHeader'>
            <ButtonIcon
              kind='ghost'
              size='medium'
              ariaLabel={l('close')}
              icon={<Icon kind='Close' />}
              onClick={handleClusterClose}
              className='Sod-ViolationsGraphCluster-listHeaderCloseBtn'
            />
          </Box>
          <Box tag='div' className='Sod-ViolationsGraphCluster-loadingWrapper'>
            <Box
              tag='div'
              className='Sod-ViolationsGraphCluster-loadingWrapperMessage'>
              <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
              <Typography kind='body2' color={CF_CRT_MED}>
                {l("Data couldn't be retrieved.")}
              </Typography>
            </Box>
            <Button
              kind='filled'
              size='small'
              onClick={() => triggerGraphCluster()}>
              {l('Refresh')}
            </Button>
          </Box>
        </>
      );
    }

    if ((clusterData === null && activeRowContext === null) || isLoading) {
      return (
        <>
          <Box className='Sod-ViolationsGraphCluster-listHeader'>
            <ButtonIcon
              kind='ghost'
              size='medium'
              ariaLabel={l('close')}
              icon={<Icon kind='Close' />}
              onClick={handleClusterClose}
              className='Sod-ViolationsGraphCluster-listHeaderCloseBtn'
            />
          </Box>
          <Box tag='div' className='Sod-ViolationsGraphCluster-loadingWrapper'>
            <Loader kind='dots' format='inline' color='brand' />
          </Box>
        </>
      );
    }

    if (activeRowContext) {
      return (
        <ViolationsGraphSidePanel
          rowContext={activeRowContext}
          isSidePanelVisible={!!activeRowContext}
          handleSidePanelBack={() => {
            if (clusterData) {
              dispatch(violationsTabActions.setGraphRowContext(null));

              return;
            }

            setClusterData(null);
            dispatch(violationsTabActions.setActiveGraphPoint(null));
          }}
          handleSidePanelClose={() => handleClusterClose()}
        />
      );
    }

    if (clusterData) {
      const total = clusterTotalRecords ?? clusterData.length;
      let clusterLabel: string;

      if (total > clusterData.length) {
        clusterLabel = l('{count} of {total} violations selected', {
          count: clusterData.length,
          total: total.toLocaleString(),
        });
      } else {
        clusterLabel = l('{count} violations selected', {
          count: clusterTotalRecords ?? 0,
        });
      }

      return (
        <>
          <Box className='Sod-ViolationsGraphCluster-listHeaderSubTab'>
            <Box className='Sod-ViolationsGraphCluster-listHeaderTextWrapper'>
              <ButtonIcon
                kind='ghost'
                size='medium'
                icon={<Icon kind='ChevronLeft' />}
                onClick={() => {
                  const isEntitlementContext =
                    window.location.pathname.includes('entitlement');

                  if (isEntitlementContext) {
                    setClusterData(null);
                    dispatch(violationsTabActions.setActiveGraphPoint(null));
                    dispatch(violationsTabActions.setGraphRowContext(null));

                    handleClusterClose();
                  } else {
                    handleClusterBack();
                  }
                }}
                ariaLabel={l('Back to Risk SodInsights List')}
              />
              <Typography kind='body2' color={CF_NEU_BLD} isNoWrap>
                {clusterLabel}
              </Typography>
            </Box>
            <ButtonIcon
              kind='ghost'
              size='medium'
              ariaLabel={l('close')}
              icon={<Icon kind='Close' />}
              onClick={() => handleClusterClose()}
            />
          </Box>
          <Box tag='div' className='Sod-ViolationsGraphCluster-list'>
            {clusterData.map((row, index) => (
              <ClusterCard
                key={row.sodKey}
                row={row}
                onClick={() => {
                  const tempRowContext = getRowContext({
                    ...row,
                    tracker: {
                      id: row.sodKey,
                      isChecked: false,
                      isRowDetailsOpen: false,
                      isTransition: false,
                      isFirstAddded: false,
                      isReadOnlyAction,
                    },
                  });

                  dispatch(
                    violationsTabActions.setGraphRowContext(tempRowContext)
                  );
                }}
                isLast={index === clusterData.length - 1}
              />
            ))}
          </Box>
          <ViolationsInsightsFooter
            isGraphMode={isGraphMode}
            onClick={() => {
              if (isGraphMode) {
                dispatch(violationsTabActions.setRenderMode(RENDER_MODES.LIST));

                return;
              }

              dispatch(violationsTabActions.setRenderMode(RENDER_MODES.GRAPH));
            }}
          />
        </>
      );
    }

    return null;
  }, [
    isError,
    clusterData,
    clusterTotalRecords,
    activeRowContext,
    isLoading,
    l,
    handleClusterClose,
    triggerGraphCluster,
    dispatch,
    isGraphMode,
    handleClusterBack,
    isReadOnlyAction,
  ]);

  const wrapperClasses = cx(
    'Sod-ViolationsGraphCluster',
    isGraphMode && 'Sod-ViolationsGraphCluster--isGraph'
  );

  return <Box className={wrapperClasses}>{renderBody()}</Box>;
}

export default ViolationsGraphCluster;
